import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  searchCustomers,
  getAiInfoCustomer,
  editKbEntry,
  archiveKbEntry,
  editNickname,
  deactivateNickname,
  promoteNickname,
  editShorthandOverride,
  deactivateShorthandOverride,
  promoteShorthand,
  upsertPalletOverride,
  deactivatePalletOverride,
  QaKbEntry,
} from '../../services/api';
import {
  AiInfoCustomer,
  AiInfoNickname,
  AiInfoShorthand,
  AiInfoPalletRow,
} from '../../types';
import { relativeTime } from './helpers';

// Customer 360 — the PRIMARY AI Info mode. A customer identity search (name /
// entity id / email via searchCustomers) -> on select, getAiInfoCustomer(id)
// renders one section per learned/derived category. Learned sections (Q&A,
// nicknames, local shorthand, pallet overrides) are editable; derived sections
// (recent items, NetSuite shorthand, netsuite/default pallet, corrections log)
// are read-only and clearly labeled by source. Per-section `errors` render a
// small "couldn't load" note so one failing section never blanks the rest.

// ---- Small shared section shell -------------------------------------------
const Section: React.FC<{
  title: string;
  subtitle?: string;
  error?: string;
  empty?: boolean;
  emptyLabel?: string;
  children?: React.ReactNode;
}> = ({ title, subtitle, error, empty, emptyLabel, children }) => (
  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
      <h3 className="text-sm font-bold text-gray-900">{title}</h3>
      {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
    </div>
    <div className="p-4">
      {error ? (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          Couldn't load this section — {error}
        </div>
      ) : empty ? (
        <div className="text-sm text-gray-400 py-2">{emptyLabel || 'Nothing here yet'}</div>
      ) : (
        children
      )}
    </div>
  </div>
);

// A source pill (override / netsuite / default / local) with consistent coloring.
const SourcePill: React.FC<{ source?: string }> = ({ source }) => {
  const s = (source || '').toLowerCase();
  const cls =
    s === 'override' || s === 'local'
      ? 'bg-blue-100 text-blue-700'
      : s === 'netsuite'
      ? 'bg-purple-100 text-purple-700'
      : 'bg-gray-100 text-gray-600';
  return <span className={'px-2 py-0.5 rounded-full text-xs font-medium ' + cls}>{source || 'default'}</span>;
};

// A scope pill: 'general' (promoted, applies to all customers) vs 'customer'
// (this customer only). Absent scope -> treated as customer-scoped.
const ScopePill: React.FC<{ scope?: string }> = ({ scope }) => {
  const isGeneral = (scope || '').toLowerCase() === 'general';
  const cls = isGeneral ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600';
  return (
    <span className={'px-2 py-0.5 rounded-full text-xs font-medium ' + cls}>
      {isGeneral ? 'general' : 'customer'}
    </span>
  );
};

// ---- Customer search box ---------------------------------------------------
const CustomerSearch: React.FC<{ onSelect: (c: { id: number; name: string }) => void }> = ({ onSelect }) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<{ id: number; name: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      const q = term.trim();
      if (q.length < 2) {
        setResults([]);
        return;
      }
      setSearching(true);
      const hits = await searchCustomers(q); // non-fatal, returns [] on failure
      setResults(hits);
      setSearching(false);
      setOpen(true);
    }, 300);
    return () => clearTimeout(t);
  }, [term]);

  // Close the dropdown on an outside click.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <input
        type="text"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        placeholder="Search a customer by name, entity id, or email…"
        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
      {open && (term.trim().length >= 2) && (
        <div className="absolute z-40 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {searching && <div className="px-3 py-2 text-sm text-gray-400">Searching…</div>}
          {!searching && results.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400">No matching customers</div>
          )}
          {results.map((c) => (
            <button
              key={c.id}
              onClick={() => {
                onSelect(c);
                setOpen(false);
                setTerm(c.name);
              }}
              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-brand-50"
            >
              {c.name} <span className="text-gray-400">#{c.id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Main ------------------------------------------------------------------
const Customer360: React.FC = () => {
  const [selected, setSelected] = useState<{ id: number; name: string } | null>(null);
  const [data, setData] = useState<AiInfoCustomer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // id (or item_id) currently mutating, to disable its row while a call is in flight.
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // Per-row promote errors (keyed like busyKey) — e.g. a blend-refused shorthand.
  // Rendered inline next to the row so a 400 never blanks the section.
  const [promoteErrors, setPromoteErrors] = useState<Record<string, string>>({});

  const load = useCallback(async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const d = await getAiInfoCustomer(id);
      setData(d);
    } catch (err) {
      setError('Failed to load customer AI info');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selected) load(selected.id);
    else setData(null);
  }, [selected, load]);

  const reload = () => { if (selected) load(selected.id); };

  // Generic action wrapper: set busy, run, refresh, surface errors non-fatally.
  const runAction = async (key: string, fn: () => Promise<any>) => {
    try {
      setBusyKey(key);
      setActionError(null);
      await fn();
      reload();
    } catch (err) {
      setActionError('Action failed — please retry');
      console.error(err);
    } finally {
      setBusyKey(null);
    }
  };

  // Promote a customer-scoped learning to general. On a refusal (e.g. 400 blend
  // guard) the backend's message is surfaced INLINE on the row (not the section
  // banner) so the rest of the section stays intact. On success, clears any prior
  // inline error and refreshes.
  const runPromote = async (key: string, fn: () => Promise<any>) => {
    try {
      setBusyKey(key);
      setPromoteErrors((m) => { const { [key]: _drop, ...rest } = m; return rest; });
      await fn();
      reload();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        (err?.response?.status === 400 ? 'Cannot promote this learning' : 'Promote failed — please retry');
      setPromoteErrors((m) => ({ ...m, [key]: msg }));
      console.error(err);
    } finally {
      setBusyKey(null);
    }
  };

  // ---- Edit modals (Q&A / nickname / shorthand / pallet) -------------------
  const [editKb, setEditKb] = useState<QaKbEntry | null>(null);
  const [kbQ, setKbQ] = useState('');
  const [kbA, setKbA] = useState('');

  const [editNick, setEditNick] = useState<AiInfoNickname | null>(null);
  const [nickVal, setNickVal] = useState('');

  const [editSh, setEditSh] = useState<AiInfoShorthand | null>(null);
  const [shVal, setShVal] = useState('');
  const [shItemName, setShItemName] = useState('');

  const [editPal, setEditPal] = useState<AiInfoPalletRow | null>(null);
  const [palVal, setPalVal] = useState('');

  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const closeModals = () => {
    setEditKb(null);
    setEditNick(null);
    setEditSh(null);
    setEditPal(null);
    setModalError(null);
    setModalSaving(false);
  };

  const saveModal = async (fn: () => Promise<any>) => {
    try {
      setModalSaving(true);
      setModalError(null);
      await fn();
      closeModals();
      reload();
    } catch (err) {
      setModalError('Failed to save changes');
      console.error(err);
    } finally {
      setModalSaving(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Search bar */}
      <div className="bg-white px-3 sm:px-6 pt-3 sm:pt-4 border-b border-gray-300">
        <div className="flex flex-wrap items-end gap-3 pb-3">
          <div className="mr-auto">
            <h2 className="text-lg font-bold text-gray-900">Customer 360</h2>
            <p className="text-xs text-gray-500">Everything the AI has learned or knows about one customer</p>
          </div>
          <CustomerSearch onSelect={setSelected} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50">
        <div className="w-full max-w-5xl mx-auto space-y-4">
          {actionError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center justify-between">
              <span>{actionError}</span>
              <button onClick={() => setActionError(null)} className="text-red-500 hover:text-red-700 ml-3">✕</button>
            </div>
          )}

          {!selected && (
            <div className="text-center py-16 text-gray-400">
              Search for a customer above to see what the AI knows about them.
            </div>
          )}

          {selected && loading && !data && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-700"></div>
            </div>
          )}

          {selected && error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
          )}

          {selected && data && (
            <>
              {/* Identity header */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-base font-bold text-gray-900">
                      {data.customer?.companyname || selected.name || 'Customer #' + selected.id}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 space-x-3">
                      {data.customer?.entityid && <span>ID {data.customer.entityid}</span>}
                      {data.customer?.email && <span>{data.customer.email}</span>}
                      {data.customer?.phone && <span>{data.customer.phone}</span>}
                      <span className="text-gray-400">#{selected.id}</span>
                    </div>
                  </div>
                  <button
                    onClick={reload}
                    className="px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 rounded-lg transition-colors"
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {/* Q&A (editable) */}
              <Section
                title="Q&A"
                subtitle="learned answers for this customer — editable"
                error={data.errors?.qa}
                empty={data.qa.length === 0}
                emptyLabel="No learned Q&A for this customer"
              >
                <div className="divide-y divide-gray-100">
                  {(data.qa as QaKbEntry[]).map((e) => {
                    const busy = busyKey === 'kb-' + e.id;
                    return (
                      <div key={e.id} className="py-2 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm text-gray-800 font-medium truncate" title={e.question_text || undefined}>
                            {e.question_text || '—'}
                          </div>
                          <div className="text-sm text-gray-600 truncate" title={e.answer_text || undefined}>
                            {e.answer_text || '—'}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            used {e.reuse_count}× · {relativeTime(e.last_used_at)}
                            {e.status === 'archived' && <span className="ml-2 text-gray-500">(archived)</span>}
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditKb(e);
                              setKbQ(e.question_text || '');
                              setKbA(e.answer_text || '');
                            }}
                            className="px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 rounded"
                          >
                            Edit
                          </button>
                          {e.status === 'archived' ? (
                            <button
                              disabled={busy}
                              onClick={() => runAction('kb-' + e.id, () => editKbEntry(e.id, { status: 'active' }))}
                              className="px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 rounded disabled:opacity-50"
                            >
                              {busy ? '…' : 'Restore'}
                            </button>
                          ) : (
                            <button
                              disabled={busy}
                              onClick={() => {
                                if (window.confirm('Archive this entry? The AI will stop using it.'))
                                  runAction('kb-' + e.id, () => archiveKbEntry(e.id));
                              }}
                              className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            >
                              {busy ? '…' : 'Archive'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Section>

              {/* Nicknames (editable) */}
              <Section
                title="Nicknames"
                subtitle="aliases the AI maps to this customer — editable"
                error={data.errors?.nicknames}
                empty={data.nicknames.length === 0}
                emptyLabel="No nicknames learned"
              >
                <div className="divide-y divide-gray-100">
                  {data.nicknames.map((n) => {
                    const busy = busyKey === 'nick-' + n.id;
                    const isGeneral = (n.scope || '').toLowerCase() === 'general';
                    const pErr = promoteErrors['nick-' + n.id];
                    return (
                      <div key={n.id} className="py-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm text-gray-800 truncate flex items-center gap-2">
                            <ScopePill scope={n.scope} />
                            <span className="font-medium">“{n.nickname}”</span>
                            {n.customer_name && <span className="text-gray-500 ml-1">→ {n.customer_name}</span>}
                          </div>
                          <div className="flex-shrink-0 flex items-center gap-1">
                            <button
                              onClick={() => { setEditNick(n); setNickVal(n.nickname || ''); }}
                              className="px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 rounded"
                            >
                              Edit
                            </button>
                            {!isGeneral && (
                              <button
                                disabled={busy}
                                title="Make this alias apply to all customers"
                                onClick={() => {
                                  if (window.confirm('Promote this nickname to general? It will apply to ALL customers.'))
                                    runPromote('nick-' + n.id, () => promoteNickname(n.id));
                                }}
                                className="px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 rounded disabled:opacity-50"
                              >
                                {busy ? '…' : 'Promote to general'}
                              </button>
                            )}
                            <button
                              disabled={busy}
                              onClick={() => {
                                if (window.confirm('Remove this nickname?'))
                                  runAction('nick-' + n.id, () => deactivateNickname(n.id));
                              }}
                              className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            >
                              {busy ? '…' : 'Remove'}
                            </button>
                          </div>
                        </div>
                        {pErr && <div className="mt-1 text-xs text-amber-700">{pErr}</div>}
                      </div>
                    );
                  })}
                </div>
              </Section>

              {/* Shorthand -> product: local (editable) + NetSuite (read-only) */}
              <Section
                title="Shorthand → product"
                subtitle="local overrides are editable · NetSuite shorthand is read-only reference"
                error={data.errors?.shorthand_local || data.errors?.shorthand_netsuite}
                empty={data.shorthand_local.length === 0 && data.shorthand_netsuite.length === 0}
                emptyLabel="No shorthand mappings"
              >
                <div className="space-y-1">
                  {data.shorthand_local.map((s) => {
                    const busy = busyKey === 'sh-' + s.id;
                    const isGeneral = (s.scope || '').toLowerCase() === 'general';
                    const pErr = promoteErrors['sh-' + s.id];
                    return (
                      <div key={'local-' + s.id} className="py-2 border-b border-gray-100">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm text-gray-800 truncate flex items-center gap-2">
                            <SourcePill source="local" />
                            <ScopePill scope={s.scope} />
                            <span className="font-medium">“{s.shorthand}”</span>
                            <span className="text-gray-500">→ {s.item_name || 'item #' + s.item_id}</span>
                            {(s.default_quantity != null || s.default_unit) && (
                              <span className="text-gray-400 text-xs">
                                ({s.default_quantity ?? ''} {s.default_unit || ''})
                              </span>
                            )}
                          </div>
                          <div className="flex-shrink-0 flex items-center gap-1">
                            <button
                              onClick={() => { setEditSh(s); setShVal(s.shorthand || ''); setShItemName(s.item_name || ''); }}
                              className="px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 rounded"
                            >
                              Edit
                            </button>
                            {!isGeneral && (
                              <button
                                disabled={busy}
                                title="Make this shorthand apply to all customers (custom blends are refused)"
                                onClick={() => {
                                  if (window.confirm('Promote this shorthand to general? It will apply to ALL customers.'))
                                    runPromote('sh-' + s.id, () => promoteShorthand(s.id));
                                }}
                                className="px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 rounded disabled:opacity-50"
                              >
                                {busy ? '…' : 'Promote to general'}
                              </button>
                            )}
                            <button
                              disabled={busy}
                              onClick={() => {
                                if (window.confirm('Remove this shorthand override?'))
                                  runAction('sh-' + s.id, () => deactivateShorthandOverride(s.id));
                              }}
                              className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            >
                              {busy ? '…' : 'Remove'}
                            </button>
                          </div>
                        </div>
                        {pErr && <div className="mt-1 text-xs text-amber-700">{pErr}</div>}
                      </div>
                    );
                  })}
                  {data.shorthand_netsuite.map((s) => (
                    <div key={'ns-' + s.id} className="py-2 flex items-center justify-between gap-3 border-b border-gray-100">
                      <div className="text-sm text-gray-700 truncate flex items-center gap-2">
                        <SourcePill source="netsuite" />
                        <span className="font-medium">“{s.shorthand}”</span>
                        <span className="text-gray-500">→ {s.item_name || 'item #' + s.item_id}</span>
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">read-only</span>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Usual products / order history (read-only, derived from NetSuite) */}
              <Section
                title="Usual products"
                subtitle="derived live from NetSuite order history — read-only"
                error={data.errors?.recent_items}
                empty={data.recent_items.length === 0}
                emptyLabel="No recent order history"
              >
                <table className="min-w-full text-sm">
                  <thead className="text-gray-400 text-xs uppercase">
                    <tr>
                      <th className="text-left font-medium pb-1">Product</th>
                      <th className="text-right font-medium pb-1">Orders</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.recent_items.map((it, i) => (
                      <tr key={(it.item_id ?? 'x') + '-' + i}>
                        <td className="py-1.5 text-gray-800 truncate max-w-[320px]" title={it.item_name || undefined}>
                          {it.item_name || 'item #' + it.item_id}
                        </td>
                        <td className="py-1.5 text-right text-gray-600">{it.order_count ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>

              {/* Pallet counts (source-labeled; overrides editable) */}
              <Section
                title="Pallet counts"
                subtitle="bags per pallet · overrides editable · netsuite/default read-only"
                error={data.errors?.pallet}
                empty={data.pallet.length === 0}
                emptyLabel="No pallet counts"
              >
                <table className="min-w-full text-sm">
                  <thead className="text-gray-400 text-xs uppercase">
                    <tr>
                      <th className="text-left font-medium pb-1">Product</th>
                      <th className="text-right font-medium pb-1">Bags/pallet</th>
                      <th className="text-left font-medium pb-1 pl-4">Source</th>
                      <th className="text-right font-medium pb-1">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.pallet.map((p, i) => {
                      const isOverride = (p.source || '').toLowerCase() === 'override';
                      const busy = busyKey === 'pal-' + p.item_id;
                      return (
                        <tr key={(p.item_id ?? 'x') + '-' + i}>
                          <td className="py-1.5 text-gray-800 truncate max-w-[320px]" title={p.item_name || undefined}>
                            {p.item_name || 'item #' + p.item_id}
                          </td>
                          <td className="py-1.5 text-right text-gray-700 font-medium">{p.bags_per_pallet ?? '—'}</td>
                          <td className="py-1.5 pl-4"><SourcePill source={p.source} /></td>
                          <td className="py-1.5 text-right whitespace-nowrap">
                            {isOverride ? (
                              <>
                                <button
                                  onClick={() => { setEditPal(p); setPalVal(String(p.bags_per_pallet ?? '')); }}
                                  className="px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 rounded"
                                >
                                  Edit
                                </button>
                                <button
                                  disabled={busy || p.item_id == null}
                                  onClick={() => {
                                    if (p.item_id != null && window.confirm('Remove this pallet override?'))
                                      runAction('pal-' + p.item_id, () => deactivatePalletOverride(p.item_id as number));
                                  }}
                                  className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                >
                                  {busy ? '…' : 'Remove'}
                                </button>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">read-only</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Section>

              {/* Corrections log (read-only) */}
              <Section
                title="Corrections log"
                subtitle="human fixes the AI recorded — read-only"
                error={data.errors?.corrections}
                empty={data.corrections.length === 0}
                emptyLabel="No corrections recorded"
              >
                <table className="min-w-full text-sm">
                  <thead className="text-gray-400 text-xs uppercase">
                    <tr>
                      <th className="text-left font-medium pb-1">Type</th>
                      <th className="text-left font-medium pb-1">Original</th>
                      <th className="text-left font-medium pb-1">Corrected</th>
                      <th className="text-left font-medium pb-1">By</th>
                      <th className="text-right font-medium pb-1">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.corrections.map((c) => (
                      <tr key={c.id}>
                        <td className="py-1.5 text-gray-600">{c.correction_type || '—'}</td>
                        <td className="py-1.5 text-gray-500 truncate max-w-[180px]" title={c.original_value || undefined}>{c.original_value || '—'}</td>
                        <td className="py-1.5 text-gray-800 truncate max-w-[180px]" title={c.corrected_value || undefined}>{c.corrected_value || '—'}</td>
                        <td className="py-1.5 text-gray-500 truncate max-w-[140px]" title={c.corrected_by || undefined}>{c.corrected_by || '—'}</td>
                        <td className="py-1.5 text-right text-gray-500">{relativeTime(c.corrected_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            </>
          )}
        </div>
      </div>

      {/* ---- Edit modals ---- */}
      {editKb && (
        <Modal title={'Edit Q&A #' + editKb.id} onClose={closeModals} saving={modalSaving} error={modalError}
          onSave={() => saveModal(() => editKbEntry(editKb.id, { question_text: kbQ, answer_text: kbA }))}>
          <Field label="Question">
            <textarea value={kbQ} onChange={(e) => setKbQ(e.target.value)} rows={2} className={inputCls} />
          </Field>
          <Field label="Answer">
            <textarea value={kbA} onChange={(e) => setKbA(e.target.value)} rows={5} className={inputCls} />
          </Field>
        </Modal>
      )}

      {editNick && (
        <Modal title="Edit nickname" onClose={closeModals} saving={modalSaving} error={modalError}
          onSave={() => saveModal(() => editNickname(editNick.id, { nickname: nickVal }))}>
          <Field label="Nickname">
            <input value={nickVal} onChange={(e) => setNickVal(e.target.value)} className={inputCls} />
          </Field>
        </Modal>
      )}

      {editSh && (
        <Modal title="Edit shorthand override" onClose={closeModals} saving={modalSaving} error={modalError}
          onSave={() => saveModal(() => editShorthandOverride(editSh.id, { shorthand: shVal, item_name: shItemName }))}>
          <Field label="Shorthand">
            <input value={shVal} onChange={(e) => setShVal(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Product name">
            <input value={shItemName} onChange={(e) => setShItemName(e.target.value)} className={inputCls} />
          </Field>
        </Modal>
      )}

      {editPal && (
        <Modal title="Edit pallet override" onClose={closeModals} saving={modalSaving} error={modalError}
          onSave={() => {
            const n = parseInt(palVal, 10);
            if (!Number.isFinite(n) || n <= 0 || editPal.item_id == null) { setModalError('Enter a positive number'); return; }
            saveModal(() => upsertPalletOverride(editPal.item_id as number, n, editPal.item_name || ''));
          }}>
          <Field label="Bags per pallet">
            <input type="number" value={palVal} onChange={(e) => setPalVal(e.target.value)} className={inputCls} />
          </Field>
        </Modal>
      )}
    </div>
  );
};

// ---- tiny modal + field primitives (kept local; mirror the v1 modal styling) --
const inputCls =
  'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{label}</label>
    {children}
  </div>
);

const Modal: React.FC<{
  title: string;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
  children: React.ReactNode;
}> = ({ title, onClose, onSave, saving, error, children }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
      </div>
      <div className="px-5 py-4 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>}
        {children}
      </div>
      <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
        <button onClick={onClose} disabled={saving}
          className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button onClick={onSave} disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-brand-700 hover:bg-brand-800 rounded-lg transition-colors disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  </div>
);

export default Customer360;
